import React from 'react';
import { SectionLabel } from '@/components/ui/section-label';
import type { Process } from '../type/Process';
import type { Machine } from '../type/Machine';
import type { Material } from '../type/Material';
import type { MaterialLot } from '../type/MaterialLot';
import type { Article } from '../type/Article';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { Button } from '@/components/ui/button';
import { Copy, Plus, Save, Workflow, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';
import { useTranslation } from '@/hooks/useTranslation';
import { ProcessFormCuttingSection } from './ProcessFormCuttingSection';
import { ProcessFormMachinedSection } from './ProcessFormMachinedSection';
import { ProcessFormOperatorSection } from './ProcessFormOperatorSection';
import { ProcessFormToolingSection } from './ProcessFormToolingSection';
import {
  useCuttingSetProcess,
  useMachinedSetProcess,
  useOperatorSetProcess,
  useToolingSetProcess,
} from '../hooks/useProcessFormAutoToggle';

type ProcessFormProps = {
  formProcess: Process;
  setFormProcess: React.Dispatch<React.SetStateAction<Process>>;
  toolReferences: string[];
  materials: Material[];
  materialLots: MaterialLot[];
  activeArticles: Article[];
  machines: Machine[];
  isEditing: boolean;
  isSaving: boolean;
  isLoading: boolean;
  onSave: () => void;
  onNew: () => void;
  onDuplicate: () => void;
};

export const ProcessForm: React.FC<ProcessFormProps> = ({
  formProcess, setFormProcess, toolReferences, materials, materialLots, activeArticles,
  machines, isEditing, isSaving, isLoading, onSave, onNew, onDuplicate,
}) => {
  const { t } = useTranslation();

  const setCutting = useCuttingSetProcess(setFormProcess);
  const setMachined = useMachinedSetProcess(setFormProcess);
  const setOperator = useOperatorSetProcess(setFormProcess);
  const setTooling = useToolingSetProcess(setFormProcess);

  const addMachine = (value: string | null) => {
    const id = Number(value);
    if (!id || formProcess.machineIds.includes(id)) return;
    setFormProcess((p) => ({ ...p, machineIds: [...p.machineIds, id] }));
  };

  const removeMachine = (id: number) =>
    setFormProcess((p) => ({ ...p, machineIds: p.machineIds.filter((x) => x !== id) }));

  const handleToggleMandatory = (key: string) => {
    setFormProcess((prev) => ({
      ...prev,
      mandatoryFields: prev.mandatoryFields.includes(key)
        ? prev.mandatoryFields.filter((f) => f !== key)
        : [...prev.mandatoryFields, key],
    }));
  };

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <SectionLabel icon={Workflow}>
          {t('traceability.processManager.form.sectionTitle')}
        </SectionLabel>
        {isEditing && formProcess.name && (
          <Badge tone="primary" className="animate-pulse">
            {t('traceability.processManager.form.editingPill', { name: formProcess.name })}
          </Badge>
        )}
      </div>

      <SimpleInput
        name="process-name"
        label={t('traceability.processManager.form.fields.name.label')}
        value={formProcess.name}
        onChange={(val) => setFormProcess((p) => ({ ...p, name: val as string }))}
        placeholder={t('traceability.processManager.form.fields.name.placeholder')}
        required
      />

      <div className="mt-4">
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">
          {t('traceability.processManager.form.fields.type.label')}
        </p>
        <SegmentedControl
          size="sm"
          aria-label={t('traceability.processManager.form.fields.type.label')}
          value={formProcess.processType}
          onChange={(type) => setFormProcess((p) => ({ ...p, processType: type }))}
          options={(['standard', 'test'] as const).map((type) => ({
            value: type,
            label: t(`traceability.processManager.form.fields.type.${type}`),
          }))}
        />
      </div>

      <div className="mt-4">
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">
          {t('traceability.processManager.form.fields.toolReference.label')}
        </p>
        <SimpleSelect
          aria-label={t('traceability.processManager.form.fields.toolReference.label')}
          value={formProcess.toolReference}
          onChange={(next) => setFormProcess((p) => ({ ...p, toolReference: next ?? '' }))}
          noneLabel={
            toolReferences.length === 0
              ? t('traceability.processManager.form.fields.toolReference.noOptions')
              : t('traceability.processManager.form.fields.toolReference.placeholder')
          }
          options={toolReferences.map((ref) => ({ value: ref, label: ref }))}
        />
      </div>

      <div className="mt-4">
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">
          {t('traceability.processManager.form.fields.material.label')}
        </p>
        <SimpleSelect
          aria-label={t('traceability.processManager.form.fields.material.label')}
          value={formProcess.material?.id != null ? String(formProcess.material.id) : null}
          onChange={(next) =>
            setFormProcess((p) => ({
              ...p,
              material: next ? (materials.find((m) => m.id === Number(next)) ?? null) : null,
              materialLot: null,
            }))
          }
          noneLabel={t('traceability.processManager.form.fields.material.none')}
          options={materials
            .filter((mat) => mat.id !== undefined)
            .map((mat) => ({ value: String(mat.id), label: mat.name }))}
        />
      </div>

      <div className="mt-4">
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">
          {t('traceability.processManager.form.fields.materialLot.label')}
        </p>
        <SimpleSelect
          aria-label={t('traceability.processManager.form.fields.materialLot.label')}
          value={formProcess.materialLot?.id != null ? String(formProcess.materialLot.id) : null}
          disabled={formProcess.material === null}
          onChange={(next) =>
            setFormProcess((p) => ({
              ...p,
              materialLot: next
                ? (materialLots.find((l) => l.id === Number(next)) ?? null)
                : null,
            }))
          }
          noneLabel={t('traceability.processManager.form.fields.materialLot.none')}
          options={materialLots
            .filter((lot) => lot.id !== undefined)
            .map((lot) => ({
              value: String(lot.id),
              label: `${lot.supplier ?? '—'} · ${lot.lotNumber ?? '—'}`,
            }))}
        />
      </div>

      <div className="mt-4">
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">
          {t('traceability.processManager.form.fields.article.label')}
        </p>
        <SimpleSelect
          aria-label={t('traceability.processManager.form.fields.article.label')}
          value={formProcess.articleId !== null ? String(formProcess.articleId) : null}
          onChange={(next) =>
            setFormProcess((p) => ({ ...p, articleId: next ? Number(next) : null }))
          }
          noneLabel={t('traceability.processManager.form.fields.article.none')}
          options={activeArticles
            .filter((a) => a.id !== undefined)
            .map((a) => ({ value: String(a.id), label: a.name }))}
        />
      </div>

      <div className="mt-4">
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">
          {t('traceability.processManager.form.fields.machines.label')}
        </p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {formProcess.machineIds.map((id) => {
            const m = machines.find((x) => x.id === id);
            return (
              <Badge key={id} tone="primary">
                {m?.name ?? String(id)}
                <button type="button" onClick={() => removeMachine(id)}
                  className="-mr-0.5 rounded-full outline-none hover:text-destructive focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  aria-label={t('common.removeItem', { item: m?.name ?? String(id) })}>
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </Badge>
            );
          })}
        </div>
        {/* Remounted after each addition so it shows the placeholder again */}
        <SimpleSelect
          key={formProcess.machineIds.join(',')}
          aria-label={t('traceability.processManager.form.fields.machines.label')}
          value={null}
          onChange={addMachine}
          placeholder={t('traceability.processManager.form.fields.machines.placeholder')}
          options={machines
            .filter((m) => m.id !== undefined && !formProcess.machineIds.includes(m.id!))
            .map((m) => ({ value: String(m.id), label: m.name }))}
        />
        {formProcess.machineIds.length === 0 && (
          <label className="mt-2 flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={formProcess.mandatoryFields.includes('machineId')}
              onChange={() => handleToggleMandatory('machineId')}
              className="h-3.5 w-3.5 rounded border-border accent-primary"
            />
            {t('traceability.processManager.form.showToOperator')}
          </label>
        )}
      </div>

      <ProcessFormCuttingSection
        formProcess={formProcess}
        setFormProcess={setCutting}
        mandatoryFields={formProcess.mandatoryFields}
        onToggleMandatory={handleToggleMandatory}
      />
      <ProcessFormMachinedSection
        formProcess={formProcess}
        setFormProcess={setMachined}
        mandatoryFields={formProcess.mandatoryFields}
        onToggleMandatory={handleToggleMandatory}
      />
      <ProcessFormOperatorSection
        formProcess={formProcess}
        setFormProcess={setOperator}
        mandatoryFields={formProcess.mandatoryFields}
        onToggleMandatory={handleToggleMandatory}
      />
      <ProcessFormToolingSection
        formProcess={formProcess}
        setFormProcess={setTooling}
        mandatoryFields={formProcess.mandatoryFields}
        onToggleMandatory={handleToggleMandatory}
      />

      {formProcess.pieceMachiningDuration === null &&
        formProcess.totalMachiningDuration === null && (
          <Callout tone="warning" className="mt-4">
            {t('traceability.processManager.form.durationWarning')}
          </Callout>
        )}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <Button className="flex-1" onClick={onSave} loading={isSaving} disabled={isLoading}>
          {isSaving
            ? t('traceability.processManager.form.submit.saving')
            : <><Save aria-hidden />{isEditing ? t('traceability.processManager.form.submit.update') : t('traceability.processManager.form.submit.save')}</>}
        </Button>
        {isEditing && (
          <Button className="flex-1" variant="outline" onClick={onDuplicate} disabled={isSaving || isLoading}>
            <Copy aria-hidden />
            {t('traceability.processManager.form.submit.duplicate')}
          </Button>
        )}
        <Button className="flex-1" variant="outline" onClick={onNew} disabled={isSaving || isLoading}>
          <Plus aria-hidden />
          {t('traceability.processManager.form.new')}
        </Button>
      </div>
    </Card>
  );
};
